/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMsal } from '@azure/msal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useSubmitSubscribeAdditionnalInformationAsync, useUploadFileAsync } from '../apis';
import { RegisterSubscribeAdditionnalInformationForm } from '../types';

export const useSubscribeAdditionnalInformationForm = () => {
  const { accounts } = useMsal();

  const navigate = useNavigate();
  const schema = useFormSchema();
  const [isSubmitSubscribeAdditionnalInformationFormInError, setIsSubmitSubscribeAdditionnalInformationFormInError] =
    useState<boolean>(false);

  const {
    mutate: submitSubscribeAdditionnalInformationAsync,
    isLoading: isSubmitSubscribeAdditionnalInformationPending,
    isSuccess: isSubmitSubscribeAdditionnalInformationeSuccess,
  } = useSubmitSubscribeAdditionnalInformationAsync();

  const {
    mutate: uploadFileAsync,
    isLoading: isUploadFileAsyncPending,
    isSuccess: isUploadFileAsynceSuccess,
  } = useUploadFileAsync();

  const { control, formState, handleSubmit, register } = useForm<RegisterSubscribeAdditionnalInformationForm>({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const handleCancelTheRequest = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleSubmitSubscribeAdditionnalInformation = handleSubmit(
    ({ kbisFile, ...data }: RegisterSubscribeAdditionnalInformationForm) => {
      submitSubscribeAdditionnalInformationAsync(
        {
          data,
        },
        {
          onError: () => {
            setIsSubmitSubscribeAdditionnalInformationFormInError(false);

            const formData = new FormData();
            formData.append('File', kbisFile);
            uploadFileAsync(
              { formData },
              {
                onSuccess: () => {
                  setIsSubmitSubscribeAdditionnalInformationFormInError(false);
                  navigate('/');
                },
                onError: () => {
                  setIsSubmitSubscribeAdditionnalInformationFormInError(true);
                },
              },
            );
          },
          onSuccess: () => {
            setIsSubmitSubscribeAdditionnalInformationFormInError(true);
          },
        },
      );
    },
  );

  const handleCloseToaster = useCallback(() => {
    setIsSubmitSubscribeAdditionnalInformationFormInError(false);
  }, []);

  return {
    isSubmitSubscribeAdditionnalInformationFormInError,
    isSubmitSubscribeAdditionnalInformationeSuccess,
    isSubmitSubscribeAdditionnalInformationPending,
    formState,
    control,
    register,
    handleCloseToaster,
    handleCancelTheRequest,
    handleSubmitSubscribeAdditionnalInformation,
  };
};

function useFormSchema() {
  const { t } = useTranslation('borderCrossingRORO_onBoarding');

  return Yup.object().shape({
    registrationNumber: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    vatNumber: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    eori: Yup.string()
      .trim()
      .nullable()
      .max(120, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 120 })),
    companyTypeId: Yup.string()
      .trim()
      .nullable()
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    paymentMethodId: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    weeklyDispatchVolume: Yup.number()
      .moreThan(-0.9999, t('borderCrossingRORO.onBoarding.form.fieldIsPositive.errorMessage'))
      .nullable()
      .transform((value: number) => (Number.isNaN(value) ? undefined : value))
      .notRequired(),
    invoicingEmail: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .email(t('borderCrossingRORO.onBoarding.form.invalidMail.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    kbisFile: Yup.mixed().required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage')),
    bankName: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    holderName: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    iban: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    bic: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
  });
}
